import type { Registry } from "@/api.js"
import { Flag } from "@/components/Flag.js"
import { Input } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { configurableCountries, type EditorCountry } from "./model.js"

/**
 * Adding a rule set for a country.
 *
 * **What can be added is derived, not typed.** A free-text box is how `DE` came
 * to exist holding a Polish VAT rule: nothing installed answers for Germany, so
 * that set was empty by construction and read as protection anyway. The choice
 * offered here is the union of what the installed sources actually serve — and
 * a source covering every country is the one case where a free-text box is
 * honest, so that is the only case that gets one.
 */
export function AddCountry({
  countries,
  registry,
  onAdd,
}: {
  countries: readonly EditorCountry[]
  registry: Registry
  onAdd: (code: string) => void
}) {
  const coverage = configurableCountries(registry)
  const present = new Set(countries.map((country) => country.code))
  const addable = coverage.codes.filter((code) => !present.has(code))

  if (coverage.anyCountry) {
    return (
      <Input
        className="w-36"
        placeholder="Dodaj kraj"
        maxLength={2}
        aria-label="Dodaj kraj"
        onKeyDown={(event) => {
          if (event.key !== "Enter") return
          const code = event.currentTarget.value.trim().toUpperCase()
          if (!/^[A-Z]{2}$/.test(code) || present.has(code)) return
          onAdd(code)
          event.currentTarget.value = ""
        }}
      />
    )
  }

  return (
    // Controlled at "" so the trigger falls back to its placeholder after each
    // pick: this is an action, not a value the control holds.
    <Select value="" onValueChange={onAdd}>
      <SelectTrigger className="w-40" aria-label="Dodaj kraj">
        <SelectValue placeholder="Dodaj kraj" />
      </SelectTrigger>
      <SelectContent>
        {/*
         * **Never disabled, even with nothing to add.** A shut control with the
         * reason in a `title` reads as broken — which is exactly how it was
         * reported — and today it is shut for every installed build, because MF
         * and GUS both serve PL and PL is always configured. Opening it and
         * saying so is the difference between a control that answers and one
         * that appears to be missing.
         */}
        {addable.length === 0 ? (
          <p className="max-w-64 px-2.5 py-2 text-[13px] leading-5 text-muted-foreground">
            Każdy kraj obsługiwany przez zainstalowane źródła jest już dodany. Kolejny pojawi się tu
            razem ze źródłem, które go obsłuży.
          </p>
        ) : (
          addable.map((code) => (
            <SelectItem key={code} value={code} className="font-mono">
              <span className="flex items-center gap-2">
                <Flag code={code} />
                {code}
              </span>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  )
}
